import {
  AllowedMimeType,
  FileValidationResult,
  UploadTicketRequest,
  UploadTicket,
  FileUploadNotification,
  FileUploadConfiguration,
} from '../types'
import type { DbProjectFileUsageType } from '@autoriz/shared/files'
import { GQL_CLIENT } from 'src/graphqlClient'
import { createFileUploadMutations } from '../services/FileUploadMutations'

/**
 * Validates a file against backend configuration constraints
 */
export function validateFile(
  file: File,
  usage: string | undefined,
  configuration: FileUploadConfiguration
): FileValidationResult {
  const errors: string[] = []
  const warnings: string[] = []

  if (!configuration) {
    errors.push('Configuration non disponible')
    return { isValid: false, errors, warnings }
  }

  // Validate filename
  if (!file.name || file.name.length === 0) {
    errors.push('Le nom de fichier ne peut pas être vide')
  }

  if (file.name.length > configuration.pathConstraints.maxFilenameLength) {
    errors.push(`Le nom de fichier est trop long (maximum ${configuration.pathConstraints.maxFilenameLength} caractères)`)
  }

  // The backend pattern is a denylist using a Unicode property escape
  // (`\p{Cc}`); it must be reconstructed with the `u` flag or `\p` would be
  // read as a literal. Note the name reaching the backend is the *sanitized*
  // one (see createUploadTicketRequest), so this only rejects names that even
  // sanitization could not rescue.
  const filenamePattern = new RegExp(configuration.pathConstraints.filenamePattern, 'u')
  if (!filenamePattern.test(file.name)) {
    errors.push('Le nom de fichier contient des caractères non autorisés (les barres obliques « / », « \\ » et les caractères de contrôle sont interdits)')
  }

  // Validate MIME type
  const mimeType = file.type as AllowedMimeType
  // No usage (unassigned upload) → only the global MIME/size rules apply.
  const usageMetadata = usage ? configuration.usageTypes[usage] : undefined

  if (!configuration.allowedMimeTypes[mimeType]) {
    errors.push(`Type de fichier non supporté: ${file.type}`)
  } else if (usageMetadata && !usageMetadata.allowedTypes.includes(mimeType)) {
    errors.push(`Type de fichier non autorisé pour ${usageMetadata.label}. Types acceptés: ${usageMetadata.allowedTypes.join(', ')}`)
  }

  // Validate file size
  const mimeTypeLimit = configuration.allowedMimeTypes[mimeType]?.maxSize || 0
  const usageLimit = (usage ? configuration.usageSizeLimits[usage] : undefined) || configuration.usageSizeLimits.default || 50 * 1024 * 1024
  const maxAllowedSize = Math.min(mimeTypeLimit, usageLimit)

  if (file.size > maxAllowedSize) {
    errors.push(`Fichier trop volumineux (${formatFileSize(file.size)}). Taille maximum autorisée: ${formatFileSize(maxAllowedSize)}`)
  }

  // Add warnings for large files
  if (file.size > 20 * 1024 * 1024) {
    warnings.push('Fichier volumineux - le téléchargement peut prendre du temps')
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  }
}

/**
 * Sanitizes a filename by removing or replacing invalid characters
 */
export function sanitizeFilename(filename: string, configuration?: FileUploadConfiguration): string {
  const maxLength = configuration?.pathConstraints.maxFilenameLength || 255

  // Mirror the backend denylist (S3_PATH_CONSTRAINTS.FILENAME_PATTERN): replace
  // only the characters the backend rejects — Unicode control characters
  // (`\p{Cc}`) and the path separators "/" and "\" — with an underscore.
  // Everything else (accents, apostrophes, commas, "@", "#", brackets, CJK, …)
  // is preserved so the user's original filename survives intact.
  return filename
    .replace(/[\p{Cc}/\\]/gu, '_')
    .substring(0, maxLength)
}

/**
 * Formats file size in human-readable format
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B'

  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`
}

/**
 * Gets the maximum allowed file size for a specific usage and MIME type
 */
export function getMaxAllowedSize(
  mimeType: AllowedMimeType,
  usage: string,
  configuration: FileUploadConfiguration
): number {
  const mimeTypeLimit = configuration.allowedMimeTypes[mimeType]?.maxSize || 0
  const usageLimit = configuration.usageSizeLimits[usage] || configuration.usageSizeLimits.default || 50 * 1024 * 1024

  return Math.min(mimeTypeLimit, usageLimit)
}

/**
 * Generates an upload ticket request payload
 */
export function createUploadTicketRequest(
  projectId: number | undefined,
  file: File,
  usage: string | undefined,
  section?: string,
  configuration?: FileUploadConfiguration,
  urbanAnalysisId?: number
): UploadTicketRequest {
  return {
    projectId,
    urbanAnalysisId,
    filename: sanitizeFilename(file.name, configuration),
    mimeType: file.type as AllowedMimeType,
    fileSize: file.size,
    // Empty usage → unassigned upload (linked with use = NULL, to-sort inbox).
    usage: (usage as DbProjectFileUsageType) || undefined,
    section,
  }
}

/**
 * Calls the backend API to generate an upload ticket using GraphQL
 */
export async function generateUploadTicket(
  request: UploadTicketRequest,
  _userId: number,
  _userRole: string
): Promise<UploadTicket> {
  const fileUploadMutations = createFileUploadMutations(GQL_CLIENT)
  return fileUploadMutations.generateUploadTicket(request)
}

/**
 * Uploads a file to S3 using the presigned URL
 */
export async function uploadFileToS3(
  file: File,
  uploadUrl: string,
  onProgress?: (progress: number) => void
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()

    // Track upload progress
    if (onProgress) {
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          const progress = (event.loaded / event.total) * 100
          onProgress(progress)
        }
      })
    }

    // Handle completion
    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve()
      } else {
        reject(new Error(`Upload failed: ${xhr.status} ${xhr.statusText} - ${xhr.responseText}`))
      }
    })

    // Handle errors
    xhr.addEventListener('error', () => {
      console.error('S3 Upload Network Error')
      reject(new Error('Upload failed: Network error'))
    })

    xhr.addEventListener('abort', () => {
      console.error('S3 Upload Cancelled')
      reject(new Error('Upload cancelled'))
    })

    // Start upload
    xhr.open('PUT', uploadUrl)
    xhr.setRequestHeader('Content-Type', file.type)
    xhr.send(file)
  })
}

/**
 * Notifies the backend that file upload is complete using GraphQL.
 * Returns `{ archiveJobId }` — populated when the uploaded file was an
 * archive that the backend queued for extraction; the caller uses that id
 * to show a progress UI.
 */
export async function notifyUploadComplete(
  notification: FileUploadNotification,
  _userId: number,
  _userRole: string
): Promise<{ archiveJobId: string | null }> {
  const fileUploadMutations = createFileUploadMutations(GQL_CLIENT)
  return fileUploadMutations.notifyFileUploaded(notification)
}

/**
 * Extracts file ID from file key
 */
export function extractFileIdFromKey(fileKey: string): string {
  // File key format: "originals/{fileId}.{extension}"
  const parts = fileKey.split('/')
  if (parts.length >= 2) {
    const filename = parts[parts.length - 1]
    return filename.split('.')[0]
  }
  throw new Error('Invalid file key format')
}
